import { Canceler } from 'axios';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useRouteMatch } from 'react-router-dom';
import { FixedTab, RouteTabs, VariableTab } from '../../components/RouteTabs';
import useBackdrop from '../../hooks/useBackdrop';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { ILuckyDraw, ISuccessResult, LuckyDrawEventType } from '../../services/types';
import { replace } from '../../services/utils';
import { RouteNames } from '../config';
import LuckyDrawList from './LuckyDrawList';
import LuckyDrawView from './LuckyDrawView';
import { getLuckyDraws, deactiveLuckyDraw, deleteLuckyDraw } from '../../services/api/LuckyDraw';

interface IProps {}

interface IParamProps {
  luckyDrawId?: string;
}

const LuckyDraw = (props: IProps & RouteComponentProps): JSX.Element => {
  const menuType = props.match.path.indexOf('giftbox') > 0 ? 'giftbox' : 'event';
  const path = menuType === 'giftbox' ? RouteNames.GiftboxLuckyDraw : RouteNames.LuckyDraw;
  const prefix = menuType === 'giftbox' ? 'Gift Box ' : '';
  const match = useRouteMatch<IParamProps>(`${RouteNames.LuckyDraw}/v/:luckyDrawId`);

  const history = useHistory();
  const luckyDrawId = Number(match?.params?.luckyDrawId);

  const initialLuckyDrawIds = luckyDrawId ? [luckyDrawId] : [];
  const [editingLuckyDrawIds, setEditingLuckyDrawIds] = useState<number[]>(initialLuckyDrawIds);

  const [luckyDraws, setLuckyDraws] = useState<ILuckyDraw[]>();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.message}`);
    },
    [snackbar],
  );

  const fetchData = async () => {
    canceler.current();

    try {
      const [fetchPromise, cancel] = getLuckyDraws(
        menuType === 'giftbox' ? [LuckyDrawEventType.Giftbox] : [LuckyDrawEventType.General, LuckyDrawEventType.Chatroom],
      );
      canceler.current = cancel;
      const result = await fetchPromise;

      setLuckyDraws(result.data.items);
    } catch (e) {
      if (api.isCancel(e)) return;
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();

    return () => canceler.current();
  }, []);

  const handleRowClick = useCallback(
    (ld: ILuckyDraw) => {
      if (!ld || !ld.id) return;

      if (!editingLuckyDrawIds.find(id => id === ld.id)) {
        setEditingLuckyDrawIds([...editingLuckyDrawIds, ld.id]);
      }
    },
    [editingLuckyDrawIds],
  );

  const handleActivationChange = useCallback(
    (ld: ILuckyDraw) => {
      const onProceed = async () => {
        if (!luckyDraws || !ld || !ld.id) return;

        const idx = luckyDraws.findIndex(l => l.id == ld.id);

        try {
          backdrop.show();
          const result = await deactiveLuckyDraw(ld.id);

          if (result.status === 200) {
            const cloned = [...luckyDraws];
            cloned[idx] = (result.data as ISuccessResult<ILuckyDraw>).data;
            setLuckyDraws(cloned);
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      if (!moment(ld.expiredAt).isAfter(moment())) {
        snackbar.error(`Cannot activate expired lucky draw!!`);
        return;
      }

      confirm.open({
        title: `Stop`,
        text: `Are you sure you want to stop this lucky draw event?`,
        onProceed: () => {
          onProceed();
        },
      });
    },
    [backdrop, confirm, snackbar, luckyDraws],
  );

  const handleDeleteClick = useCallback(
    (ld: ILuckyDraw) => {
      if (moment(ld.expiredAt).isAfter(moment())) {
        return;
      }

      const onProceed = async () => {
        if (!luckyDraws || !ld.id) return;

        const idx = luckyDraws.findIndex(l => l.id === ld.id);

        try {
          backdrop.show();
          const result = await deleteLuckyDraw(ld.id);

          if (result.status === 200) {
            const cloned = [...luckyDraws];
            cloned.splice(idx, 1);

            setLuckyDraws(cloned);

            snackbar.success('Deleted successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this lucky draw?',
        onProceed,
      });
    },
    [backdrop, confirm, snackbar, luckyDraws],
  );

  const handleCreate = useCallback(
    data => {
      if (luckyDraws) {
        setLuckyDraws([data, ...luckyDraws]);
      }

      snackbar.success('Saved successfully.');

      history.push(menuType === 'giftbox' ? RouteNames.GiftboxLuckyDraw : RouteNames.LuckyDraw);

      fetchData();
    },
    [history, snackbar, luckyDraws],
  );

  const handleTabClose = useCallback(
    luckyDrawId => {
      setEditingLuckyDrawIds(editingLuckyDrawIds.filter(id => id !== luckyDrawId));
    },
    [editingLuckyDrawIds],
  );

  const getTabLabel = useCallback(
    luckyDrawId => {
      const ld = luckyDraws?.find(c => c.id === luckyDrawId);
      return ld ? ld.title : `VIEW ${luckyDrawId}`;
    },
    [luckyDraws],
  );

  const handleEdit = useCallback(
    data => {
      if (luckyDraws) {
        const replaced = replace(luckyDraws, ld => ld.id === data.id, data);
        setLuckyDraws(replaced);
      }

      snackbar.success('Saved changes successfully.');
    },
    [snackbar, luckyDraws],
  );

  const handleActivationOnEditClick = useCallback(
    (luckyDrawId: number) => {
      const onProceed = async () => {
        if (!luckyDraws) return;

        const idx = luckyDraws.findIndex(l => l.id == luckyDrawId);

        try {
          backdrop.show();
          const result = await deactiveLuckyDraw(luckyDrawId);

          if (result.status === 200) {
            const cloned = [...luckyDraws];
            cloned[idx] = (result.data as ISuccessResult<ILuckyDraw>).data;
            setLuckyDraws(cloned);
            history.push(menuType === 'event' ? RouteNames.LuckyDraw : RouteNames.GiftboxLuckyDraw);
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: `Stop`,
        text: `Are you sure you want to stop this lucky draw event?`,
        onProceed: () => {
          onProceed();
        },
      });
    },
    [backdrop, confirm, luckyDraws, snackbar, history],
  );

  const handleDeleteOnEditClick = useCallback(
    (luckyDrawId: number) => {
      const onProceed = async () => {
        if (!luckyDraws) return;
        const idx = luckyDraws.findIndex(l => l.id === luckyDrawId);

        try {
          backdrop.show();
          const result = await deleteLuckyDraw(luckyDrawId);

          if (result.status === 200) {
            const cloned = [...luckyDraws];
            cloned.splice(idx, 1);

            setLuckyDraws(cloned);
            setEditingLuckyDrawIds(editingLuckyDrawIds.filter(id => id !== luckyDrawId));

            snackbar.success('Deleted successfully.');
          } else {
            snackbar.error(`An error has occurred. ${result.statusText}`);
          }
        } catch (e) {
          console.error(e);
          snackbar.error(`An error has occurred. ${e.message}`);
        } finally {
          backdrop.hide();
        }
      };

      confirm.open({
        title: 'Delete',
        text: 'Are you sure you want to delete this lucky draw?',
        onProceed,
      });
    },
    [backdrop, confirm, luckyDraws, snackbar, editingLuckyDrawIds],
  );

  const getTabUri = useCallback(luckyDrawId => `v/${luckyDrawId}`, []);

  const keyExtractor = useCallback(luckyDrawId => `tab-floating-${luckyDrawId}`, []);

  const variableTabRenderer = useCallback(
    luckyDrawId => (
      <LuckyDrawView
        menuType={menuType}
        luckyDrawId={luckyDrawId}
        onCreated={handleEdit}
        onError={handleError}
        onChangeActivation={handleActivationOnEditClick}
        onClickDeleteButton={handleDeleteOnEditClick}
      />
    ),

    [handleActivationOnEditClick, handleDeleteOnEditClick, handleEdit, handleError],
  );

  return (
    <RouteTabs path={path}>
      <FixedTab label={`All ${prefix}Lucky draws`}>
        <LuckyDrawList
          menuType={menuType}
          luckyDraws={luckyDraws}
          onClickRow={handleRowClick}
          onChangeActivation={handleActivationChange}
          onClickDeleteButton={handleDeleteClick}
        />
      </FixedTab>
      <FixedTab label={`Create a ${prefix}Lucky draw`} uri="create">
        <LuckyDrawView menuType={menuType} onCreated={handleCreate} onError={handleError} />
      </FixedTab>
      <VariableTab<number>
        data={editingLuckyDrawIds}
        label={getTabLabel}
        uri={getTabUri}
        onClose={handleTabClose}
        keyExtractor={keyExtractor}
        render={variableTabRenderer}
      />
    </RouteTabs>
  );
};

export default LuckyDraw;
